import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, matchPath } from 'react-router-dom';
import {
    AppShell,
    Burger,
    Group,
    Menu,
    NavLink,
    Stack,
    Text,
    Container,
    Divider,
    Drawer,
    Switch,
    useMantineColorScheme,
    ThemeIcon,
    Button,
    Avatar,
    UnstyledButton,
    Tooltip,
    Modal,
    SimpleGrid,
    Anchor,
    useComputedColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSWRConfig } from 'swr';
import Home from '../components/Home';
import LogInForm from '../components/authentication/LogInForm';
import AcceptInviteForm from '../components/authentication/AcceptInviteForm';
import ResetPasswordForm from '../components/authentication/ResetPasswordForm';

import Locations from '../components/location/Locations';
import LocationDetail from '../components/location/LocationDetail';
import LocationForm from '../components/location/LocationForm';

import DeviceTagsList from '../components/deviceTag/DeviceTagsList';
import DeviceTagForm from '../components/deviceTag/DeviceTagForm';
import DeviceTagDetail from '../components/deviceTag/DeviceTagDetail';

import Devices from '../components/device/Devices';
import DeviceDetail from '../components/device/DeviceDetail';
import DeviceForm from '../components/device/DeviceForm';

import PasswordRecoveryForm from '../components/authentication/PasswordRecoveryForm';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useSessionKeepalive } from '../hooks/useSessionKeepalive';
import { useAnalytics } from '../hooks/useAnalytics';
import { track } from '../utils/mixpanel';
import { AnalyticsEvents } from '../analytics/events';
import {
    IconChartHistogram,
    IconBuildings,
    IconDevicesPc,
    IconLogout2,
    IconMoonStars,
    IconPresentation,
    IconTags,
    IconCurrencyDollar,
    IconChartLine,
    IconFileText,
    IconBriefcase,
    IconBuildingStore,
    IconCirclePlus,
    IconUser,
    IconMapPin,
    IconSettings,
    IconUsers,
    IconDeviceTv,
    IconChevronDown,
    IconLayout,
    IconBox,
    IconListDetails,
    IconLayoutGrid,
} from '@tabler/icons-react';
import Logo from '../components/Logo';
import './AppRoutes.css';
import DeviceClaimForm from '../components/device/DeviceClaimForm';
import DeviceConfigForm from '../components/deviceConfig/DeviceConfigForm';
import DeviceV2ConfigForm from '../components/shell/DeviceV2ConfigForm';
import ComponentForm from '../components/shell/ComponentForm';
import SceneForm from '../components/shell/SceneForm';
import PlaylistsPage from '../components/shell/studio/PlaylistsPage';
import { QrProxy } from '../components/QrProxy';
import PricingTabs from '../components/pricing/PricingTabs';
import MediaCalculator from '../components/calculator/MediaCalculator';
import Pitches from '../components/pitches/Pitches';
import Demos from '../components/demos/Demos';
import Marketplace from '../components/marketplace/Marketplace';
import Profile from '../components/Profile';
import OrgSettings from '../components/OrgSettings';
import OrgSwitcher from '../components/OrgSwitcher';
import { GetStartedItem } from '../components/GetStartedItem';
import { NewCustomerModal } from '../components/organizations/NewCustomerModal';
import { AdminUsersPage } from '../components/admin/AdminUsersPage';
import { AdminOrganizationsPage } from '../components/admin/AdminOrganizationsPage';
import { AdminDevicesPage } from '../components/admin/AdminDevicesPage';
import MembersPage from '../components/members/MembersPage';
import Can from '../components/Can';
import HyperspaceOverlay from '../components/HyperspaceOverlay';
import { appConfig } from '../data/AppConfig';
import { resolveAvatarSrc } from '../data/avatarUrl';

const getInitials = (name: string) => {
    // Split the string into words
    const words = name.trim().split(/\s+/);

    // Extract the first letter of the first two words, if available
    const initials = words.slice(0, 2).map(word => word[0].toUpperCase());

    // Join the initials into a string and return
    return initials.join('');
};

export const AppRoutes: React.FC = () => {
    const [opened, { toggle }] = useDisclosure();
    const [userMenuOpened, setUserMenuOpened] = useState(false);
    const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
    const [componentDrawerOpened, { open: openComponentDrawer, close: closeComponentDrawer }] =
        useDisclosure(false);
    const [sceneDrawerOpened, { open: openSceneDrawer, close: closeSceneDrawer }] = useDisclosure(false);
    const [newCustomerOpened, { open: openNewCustomerModal, close: closeNewCustomerModal }] =
        useDisclosure(false);
    const { user, organization: homeOrg, activeOrg, logout } = useAuth();
    const { isStaff, showSalesTools, showAdminTools } = usePermissions();

    /**
     * The nav header names whichever org the session is acting against, so it
     * matches what the pages below it are actually showing. Falls back to the
     * home org before /me resolves.
     */
    const displayOrg = activeOrg ?? homeOrg;
    useSessionKeepalive(!!user);
    // App-level analytics: page views on every route change, plus Mixpanel
    // identity + org super properties that follow the signed-in session.
    useAnalytics({ user, organization: displayOrg, isStaff });
    const navigate = useNavigate();
    const { mutate } = useSWRConfig();
    const { colorScheme, setColorScheme } = useMantineColorScheme();
    const location = useLocation();
    const { pathname } = location;
    // Every page that renders its own AuthLayout. A signed-in user landing on one
    // of these (following a reset link, say) must not get the app chrome wrapped
    // around a full-bleed auth shell.
    const hideNav = ['/sign-in', '/recover-password', '/reset-password', '/accept-invite'].some(
        route => !!matchPath(route, pathname),
    );
    const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
    const darkerInLight = scheme === 'light' ? '#000' : '#FFF';

    const handleLogout = () => {
        void logout();
    };

    const handleNav = (route: string) => {
        navigate(route);
        toggle();
    };

    useEffect(() => {
        // const onAuthRoute =
        //     !!matchPath('/sign-in', pathname) ||
        //     !!matchPath('/sign-up', pathname) ||
        //     !!matchPath('/recover-password', pathname);
        // if (onAuthRoute) {
        //     setColorScheme('dark');
        //     return;
        // }
        const saved = localStorage.getItem('color-scheme');
        if (saved === 'light' || saved === 'dark') setColorScheme(saved);
    }, [pathname, setColorScheme]);

    const useDarkModeToggle = () => {
        const mode = colorScheme === 'dark' ? 'light' : 'dark';
        setColorScheme(mode);
        localStorage.setItem('color-scheme', mode);
        track(AnalyticsEvents.ColorSchemeToggled, { scheme: mode });
    };

    const handleOpenCreateModal = () => {
        track(AnalyticsEvents.GetStartedOpened);
        openCreateModal();
    };

    function isActivePath(pathname: string, target: string, end: boolean = false) {
        return !!matchPath({ path: target, end }, pathname);
    }

    /**
     * Staff acting inside a customer org. The whole chrome shifts amber so a
     * borrowed session never looks like a normal one — every action here writes
     * to someone else's data.
     */
    const isSwitched = !!activeOrg && !!homeOrg && activeOrg.id !== homeOrg.id;

    /**
     * Chrome stops, in one place. `top` is also painted onto the corner spoof
     * that masks the header/navbar junction — keeping it derived from the same
     * value is why the corner can't fall out of sync with the gradients.
     */
    const chrome = isSwitched
        ? scheme === 'dark'
            ? { top: '#4a3a1e', header: '#3a2f1c', nav: '#302719' }
            : { top: '#f0dcae', header: '#fbf1dd', nav: '#fbf1dd' }
        : scheme === 'dark'
          ? { top: '#2f2f33', header: '#26262a', nav: '#1f1f23' }
          : { top: '#dee2e6', header: '#f1f3f5', nav: '#f1f3f5' };

    const headerGradient = `linear-gradient(135deg, ${chrome.top} 0%, ${chrome.header} 100%)`;

    const navGradient = `linear-gradient(180deg, ${chrome.top} 0%, ${chrome.nav} ${
        scheme === 'dark' ? '100%' : '60%'
    })`;

    const navIconColor = scheme === 'dark' ? 'gray' : 'dark';

    return (
        <AppShell
            layout="alt"
            header={{ height: !hideNav && user ? 60 : 0 }}
            navbar={{
                width: !hideNav && user ? 240 : 0,
                breakpoint: 'sm',
                collapsed: { mobile: !opened },
            }}
            padding="0"
        >
            {!hideNav && user ? (
                <>
                    <AppShell.Header style={{ background: headerGradient }}>
                        <Group h="100%" px="md" justify="space-between">
                            <Group hiddenFrom="sm" gap="sm">
                                <Burger opened={opened} onClick={toggle} size="sm" />
                                <Logo />
                            </Group>
                            <OrgSwitcher />
                            <Group ml="auto" gap={16} visibleFrom="sm">
                                <Menu shadow="md" opened={userMenuOpened} onChange={setUserMenuOpened}>
                                    <Menu.Target>
                                        <Tooltip
                                            label={user?.name ?? ''}
                                            disabled={!user?.name || userMenuOpened}
                                            position="left"
                                            withArrow
                                        >
                                            <UnstyledButton style={{ borderRadius: '50%' }}>
                                                <Avatar
                                                    size="2rem"
                                                    src={resolveAvatarSrc(user?.avatarUrl)}
                                                    style={{
                                                        background: user?.avatarUrl
                                                            ? undefined
                                                            : 'linear-gradient(135deg, #868e96 0%, #343a40 100%)',
                                                    }}
                                                >
                                                    <Text fw={700} size="xs" style={{ color: '#FFF' }}>
                                                        {user?.name ? getInitials(user?.name) : 'NA'}
                                                    </Text>
                                                </Avatar>
                                            </UnstyledButton>
                                        </Tooltip>
                                    </Menu.Target>
                                    <Menu.Dropdown style={{ minWidth: 150 }}>
                                        <Menu.Item
                                            leftSection={<IconUser size={14} />}
                                            onClick={() => navigate('/profile')}
                                        >
                                            Profile
                                        </Menu.Item>
                                        <Menu.Item
                                            leftSection={<IconMoonStars size={14} />}
                                            closeMenuOnClick={false}
                                            onClick={useDarkModeToggle}
                                            rightSection={
                                                /*
                                                 * Display only — the Menu.Item owns the toggle.
                                                 * Mantine's Switch is a <label> wrapping an
                                                 * <input>, so a click on the track is forwarded to
                                                 * the input: giving it its own onChange as well as
                                                 * the row's onClick fired the toggle twice and made
                                                 * the theme thrash. pointer-events: none lets the
                                                 * click fall through to the row, so clicking the
                                                 * switch still works — via a single handler.
                                                 */
                                                <Switch
                                                    size="xs"
                                                    checked={colorScheme === 'dark'}
                                                    readOnly
                                                    tabIndex={-1}
                                                    aria-hidden
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                            }
                                        >
                                            Dark Mode
                                        </Menu.Item>
                                        <Menu.Item leftSection={<IconLogout2 size={14} />} onClick={handleLogout}>
                                            Logout
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            </Group>
                        </Group>
                    </AppShell.Header>
                    <AppShell.Navbar p="sm" style={{ background: navGradient }}>
                        <Stack h="100%" align="stretch" justify="space-between" gap="md">
                            <div>
                                <Group h={44} pr="xs" mb="xs">
                                    <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
                                    <Menu shadow="md" position="bottom-start" width={220}>
                                        <Menu.Target>
                                            <UnstyledButton>
                                                <Group gap={8} wrap="nowrap">
                                                    {displayOrg?.avatarUrl && (
                                                        <Avatar
                                                            size="2.25rem"
                                                            radius="sm"
                                                            src={resolveAvatarSrc(displayOrg.avatarUrl)}
                                                        />
                                                    )}
                                                    <Text
                                                        size="lg"
                                                        fw={700}
                                                        style={{ fontFamily: "'Poppins', sans-serif" }}
                                                    >
                                                        {displayOrg?.name}
                                                    </Text>
                                                    <IconChevronDown size="1rem" stroke={1.5} />
                                                </Group>
                                            </UnstyledButton>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                leftSection={<IconSettings size={14} />}
                                                onClick={() => navigate('/organization/settings')}
                                            >
                                                Settings
                                            </Menu.Item>
                                            <Can permission="users.manage">
                                                <Menu.Item
                                                    leftSection={<IconUsers size={14} />}
                                                    onClick={() => navigate('/organization/members')}
                                                >
                                                    Members
                                                </Menu.Item>
                                            </Can>
                                        </Menu.Dropdown>
                                    </Menu>
                                </Group>
                                <div style={{ marginBottom: 'var(--mantine-spacing-md)' }}>
                                    <HyperspaceOverlay
                                        starCount={20}
                                        borderWidth={2}
                                        colors={
                                            scheme === 'dark'
                                                ? [
                                                      'rgba(255,255,255,0.55)',
                                                      'rgba(209,213,219,0.4)',
                                                      'rgba(156,163,175,0.3)',
                                                  ]
                                                : [
                                                      'rgba(55,65,81,0.45)',
                                                      'rgba(107,114,128,0.3)',
                                                      'rgba(156,163,175,0.25)',
                                                  ]
                                        }
                                    >
                                        <Button
                                            variant="default"
                                            size="xs"
                                            fullWidth
                                            className="gradient-border-btn"
                                            leftSection={<IconCirclePlus size="0.9rem" stroke={2.5} />}
                                            onClick={handleOpenCreateModal}
                                            style={{
                                                background: scheme === 'dark' ? '#000' : '#fff',
                                                border: `2px solid ${scheme === 'dark' ? '#5c5f66' : '#ced4da'}`,
                                                color: scheme === 'dark' ? '#FFF' : '#000',
                                            }}
                                        >
                                            Get Started
                                        </Button>
                                    </HyperspaceOverlay>
                                </div>
                                <Stack gap={4}>
                                    <NavLink
                                        label="My Screens"
                                        active={
                                            isActivePath(pathname, '/', true) ||
                                            isActivePath(pathname, '/devices') ||
                                            isActivePath(pathname, '/device/:deviceId/*')
                                        }
                                        onClick={() => handleNav('/')}
                                        leftSection={
                                            <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                <IconDevicesPc size="1.3rem" stroke={1.5} />
                                            </ThemeIcon>
                                        }
                                    />
                                    {/* <NavLink
                                        label="My Locations"
                                        active={
                                            isActivePath(pathname, '/locations') ||
                                            isActivePath(pathname, '/location/:locationId/*')
                                        }
                                        onClick={() => handleNav('/locations')}
                                        leftSection={
                                            <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                <IconBuildings size="1.3rem" stroke={1.5} />
                                            </ThemeIcon>
                                        }
                                    /> */}
                                    {/* <NavLink
                                        label="Device tags"
                                        active={
                                            isActivePath(pathname, '/device-tags') ||
                                            isActivePath(pathname, '/device-tag/:deviceTagId')
                                        }
                                        onClick={() => handleNav('/device-tags')}
                                        leftSection={
                                            <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                <IconTags size="1.3rem" stroke={1.5} />
                                            </ThemeIcon>
                                        }
                                    /> */}
                                    <NavLink
                                        label="Playlists"
                                        active={
                                            isActivePath(pathname, '/playlists') ||
                                            isActivePath(pathname, '/studio')
                                        }
                                        onClick={() => handleNav('/playlists')}
                                        leftSection={
                                            <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                <IconLayoutGrid size="1.3rem" stroke={1.5} />
                                            </ThemeIcon>
                                        }
                                    />
                                    <NavLink
                                        label="Insights"
                                        active={isActivePath(pathname, '/insights', true)}
                                        onClick={() => handleNav('/insights')}
                                        leftSection={
                                            <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                <IconChartHistogram size="1.3rem" stroke={1.5} />
                                            </ThemeIcon>
                                        }
                                    />
                                    {/* Watchly staff only for now — the partner list isn't
                                        ready for customers. The route stays mounted, so the
                                        page is still reachable by URL. */}
                                    {isStaff && (
                                        <NavLink
                                            label="Marketplace"
                                            active={isActivePath(pathname, '/marketplace', true)}
                                            onClick={() => handleNav('/marketplace')}
                                            leftSection={
                                                <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                    <IconBuildingStore size="1.3rem" stroke={1.5} />
                                                </ThemeIcon>
                                            }
                                        />
                                    )}
                                    {/* Watchly staff only — pricing, calculator and pitch
                                        material are internal selling tools, not customer
                                        features. Also hidden while switched into a
                                        customer org (see showSalesTools). */}
                                    {showSalesTools && (
                                        <>
                                            <Divider
                                                mt="md"
                                                mb={4}
                                                color={scheme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}
                                            />
                                            <NavLink
                                                label={
                                                    <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                                                        Sales Tools
                                                    </Text>
                                                }
                                            >
                                                <NavLink
                                                    label="Pricing"
                                                    active={isActivePath(pathname, '/pricing')}
                                                    onClick={() => handleNav('/pricing')}
                                                    leftSection={
                                                        <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                            <IconCurrencyDollar size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                                <NavLink
                                                    label="Calculator"
                                                    active={isActivePath(pathname, '/calculator', true)}
                                                    onClick={() => handleNav('/calculator')}
                                                    leftSection={
                                                        <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                            <IconChartLine size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                                <NavLink
                                                    label="Demos"
                                                    active={isActivePath(pathname, '/demos', true)}
                                                    onClick={() => handleNav('/demos')}
                                                    leftSection={
                                                        <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                            <IconPresentation size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                                <NavLink
                                                    label="Pitches"
                                                    active={isActivePath(pathname, '/pitches', true)}
                                                    onClick={() => handleNav('/pitches')}
                                                    leftSection={
                                                        <ThemeIcon radius="md" size="lg" color={navIconColor} variant="light">
                                                            <IconFileText size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                            </NavLink>
                                        </>
                                    )}
                                    {showAdminTools && (
                                        <>
                                            <Divider
                                                mt="md"
                                                mb={4}
                                                color={
                                                    scheme === 'dark'
                                                        ? 'rgba(255,255,255,0.15)'
                                                        : 'rgba(0,0,0,0.08)'
                                                }
                                            />
                                            <NavLink
                                                label={
                                                    <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                                                        Admin
                                                    </Text>
                                                }
                                            >
                                                <NavLink
                                                    label="Users"
                                                    active={isActivePath(pathname, '/admin/users', true)}
                                                    onClick={() => handleNav('/admin/users')}
                                                    leftSection={
                                                        <ThemeIcon
                                                            radius="md"
                                                            size="lg"
                                                            color={navIconColor}
                                                            variant="light"
                                                        >
                                                            <IconUsers size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                                <NavLink
                                                    label="Organizations"
                                                    active={isActivePath(pathname, '/admin/organizations', true)}
                                                    onClick={() => handleNav('/admin/organizations')}
                                                    leftSection={
                                                        <ThemeIcon
                                                            radius="md"
                                                            size="lg"
                                                            color={navIconColor}
                                                            variant="light"
                                                        >
                                                            <IconBuildings size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                                <NavLink
                                                    label="Devices"
                                                    active={isActivePath(pathname, '/admin/devices', true)}
                                                    onClick={() => handleNav('/admin/devices')}
                                                    leftSection={
                                                        <ThemeIcon
                                                            radius="md"
                                                            size="lg"
                                                            color={navIconColor}
                                                            variant="light"
                                                        >
                                                            <IconDeviceTv size="1.3rem" stroke={1.5} />
                                                        </ThemeIcon>
                                                    }
                                                />
                                            </NavLink>
                                        </>
                                    )}
                                </Stack>
                            </div>
                            <Stack px="xs" gap={8} align="stretch">
                                <Stack
                                    gap={4}
                                    p={6}
                                    align="center"
                                    style={{
                                        border: '1px solid var(--mantine-color-default-border)',
                                        borderRadius: 'var(--mantine-radius-md)',
                                        background: 'var(--mantine-color-default-hover)',
                                    }}
                                >
                                    <Logo noMargin />
                                    {/* <Group gap={6} justify="center" wrap="nowrap" style={{ fontSize: 10 }}>
                                        <Anchor
                                            c="dimmed"
                                            href="https://watchly.ai/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                                        >
                                            Terms
                                        </Anchor>
                                        <Text c="dimmed" style={{ fontSize: 10 }}>·</Text>
                                        <Anchor
                                            c="dimmed"
                                            href="https://watchly.ai/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                                        >
                                            Privacy
                                        </Anchor>
                                        <Text c="dimmed" style={{ fontSize: 10 }}>·</Text>
                                        <Anchor
                                            c="dimmed"
                                            href="mailto:feedback@watchly.ai"
                                            style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                                        >
                                            Feedback
                                        </Anchor>
                                    </Group> */}
                                    <Text
                                        c="dimmed"
                                        ta="center"
                                        style={{
                                            fontFamily: "'Consolas', 'Menlo', 'Monaco', monospace",
                                            fontSize: 9,
                                        }}
                                    >
                                        © {new Date().getFullYear()} WATCHLY LLC · watchly.ai
                                    </Text>
                                </Stack>
                            </Stack>
                        </Stack>
                    </AppShell.Navbar>
                    <div
                        className="main-corner-spoof"
                        style={{ background: chrome.top }}
                        aria-hidden
                    >
                        <div className="main-corner-spoof-inner" style={{ background: 'var(--mantine-color-body)' }} />
                    </div>
                </>
            ) : (
                ''
            )}
            <AppShell.Main>
                <Routes>
                    <Route path="/qr-proxy" element={<QrProxy />} />
                    {/*
                     * Auth pages render their own full-bleed AuthLayout, so unlike
                     * every other route they are NOT wrapped in <Container> — that
                     * would impose the theme's page padding and max width on a
                     * 100vh two-column grid.
                     */}
                    <Route path="/sign-in" element={<LogInForm />} />
                    <Route path="/recover-password" element={<PasswordRecoveryForm />} />
                    <Route path="/reset-password" element={<ResetPasswordForm />} />
                    <Route path="/accept-invite" element={<AcceptInviteForm />} />
                    {/* Devices is the home landing. */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Devices />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    {/* The dashboard formerly at "/". */}
                    <Route
                        path="/insights"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Home />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Profile />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/settings"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <OrgSettings />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/members"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <MembersPage />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    {/* Admin pages gate on showAdminTools internally (super admin *and* not
                        switched into a customer); the API is the real boundary. */}
                    <Route
                        path="/admin/users"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <AdminUsersPage />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/organizations"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <AdminOrganizationsPage />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/devices"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <AdminDevicesPage />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/marketplace"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Marketplace />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/pricing"
                        element={
                            <ProtectedRoute>
                                <Container fluid px="md" pb="md">
                                    <PricingTabs />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/calculator"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <MediaCalculator />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/pitches"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Pitches />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/demos"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Demos />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/locations"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <Locations />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/locations/new"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <LocationForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/location/:locationId"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <LocationDetail />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/location/:locationId/edit"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <LocationForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device-tags"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceTagsList />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device-tags/new"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceTagForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device-tag/:deviceTagId"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceTagDetail />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    {/* Devices moved to the home route "/"; keep the old path working. */}
                    <Route path="/devices" element={<Navigate to="/" replace />} />
                    <Route
                        path="/devices/new"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device/claim"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceClaimForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device/:deviceId/claim"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceClaimForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device/:deviceId"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceDetail />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device/:deviceId/edit"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device/:deviceId/config"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceConfigForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device/:deviceId/v2config"
                        element={
                            <ProtectedRoute>
                                <Container>
                                    <DeviceV2ConfigForm />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/playlists"
                        element={
                            <ProtectedRoute>
                                <Container fluid px="md">
                                    <PlaylistsPage />
                                </Container>
                            </ProtectedRoute>
                        }
                    />
                    {/* Legacy studio hub — the studio is now playlist-forward. */}
                    <Route path="/studio/*" element={<Navigate to="/playlists" replace />} />
                </Routes>
            </AppShell.Main>
            <Modal
                opened={createModalOpened}
                onClose={closeCreateModal}
                title={<Text fw={700}>Get Started</Text>}
                centered
                size="md"
            >
                <SimpleGrid cols={1} spacing="sm">
                    {/* Provisioning a customer is a platform-admin act, so it drops
                        out of Get Started while viewing one. */}
                    {showAdminTools && (
                        <GetStartedItem
                            icon={<IconBuildings size="1.4rem" stroke={1.8} />}
                            title="New Customer"
                            subtitle="Create a new organization"
                            onClick={() => {
                                track(AnalyticsEvents.GetStartedItemClicked, { item: 'New Customer' });
                                closeCreateModal();
                                openNewCustomerModal();
                            }}
                        />
                    )}
                    <GetStartedItem
                        icon={<IconMapPin size="1.4rem" stroke={1.8} />}
                        title="Add Location"
                        subtitle="Create a new location for your devices"
                        onClick={() => {
                            track(AnalyticsEvents.GetStartedItemClicked, { item: 'Add Location' });
                            closeCreateModal();
                            navigate('/locations/new');
                        }}
                    />
                    <GetStartedItem
                        icon={<IconDevicesPc size="1.4rem" stroke={1.8} />}
                        title="Claim Device"
                        subtitle="Link an existing device to your account"
                        onClick={() => {
                            track(AnalyticsEvents.GetStartedItemClicked, { item: 'Claim Device' });
                            closeCreateModal();
                            navigate('/device/claim');
                        }}
                    />
                    <GetStartedItem
                        icon={<IconBox size="1.4rem" stroke={1.8} />}
                        title="Create a Component"
                        subtitle="Add a partner page for the App Shell"
                        onClick={() => {
                            track(AnalyticsEvents.GetStartedItemClicked, { item: 'Create a Component' });
                            closeCreateModal();
                            openComponentDrawer();
                        }}
                    />
                    <GetStartedItem
                        icon={<IconLayout size="1.4rem" stroke={1.8} />}
                        title="Create a Scene"
                        subtitle="Layout Components into a Scene template"
                        onClick={() => {
                            track(AnalyticsEvents.GetStartedItemClicked, { item: 'Create a Scene' });
                            closeCreateModal();
                            openSceneDrawer();
                        }}
                    />
                    <GetStartedItem
                        icon={<IconListDetails size="1.4rem" stroke={1.8} />}
                        title="Create a Playlist"
                        subtitle="Sequence Scenes into a Playlist for a device"
                        onClick={() => {
                            track(AnalyticsEvents.GetStartedItemClicked, { item: 'Create a Playlist' });
                            closeCreateModal();
                            navigate('/playlists');
                        }}
                    />
                </SimpleGrid>
            </Modal>
            <NewCustomerModal opened={newCustomerOpened} onClose={closeNewCustomerModal} />
            <Drawer
                opened={componentDrawerOpened}
                onClose={closeComponentDrawer}
                title="Create a Component"
                position="right"
                size="lg"
                padding="md"
                overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
            >
                <ComponentForm
                    onSaved={() => void mutate('api/components')}
                    onClose={closeComponentDrawer}
                    showStudioLink
                />
            </Drawer>
            <Drawer
                opened={sceneDrawerOpened}
                onClose={closeSceneDrawer}
                title="Create a Scene"
                position="right"
                size="lg"
                padding="md"
                overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
            >
                <SceneForm
                    onSaved={() => void mutate('api/scenes')}
                    onClose={closeSceneDrawer}
                    showStudioLink
                />
            </Drawer>
        </AppShell>
    );
};
